import React, { useEffect, useCallback, useState } from 'react';
import { CheckInPhoto } from '../../types';

interface Props {
  photos: CheckInPhoto[];
  /** Preloaded object URLs indexed in the same order as `photos`. */
  objectUrls: Record<string, string>;
  initialIndex: number;
  onClose: () => void;
  /** If provided, a Delete button is shown. Athletes only — omit for coach view. */
  onDelete?: (photo: CheckInPhoto) => void;
}

const PhotoLightbox: React.FC<Props> = ({
  photos,
  objectUrls,
  initialIndex,
  onClose,
  onDelete,
}) => {
  const [index, setIndex] = useState(initialIndex);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const photo = photos[index];

  const prev = useCallback(() => {
    setConfirmingDelete(false);
    setIndex(i => Math.max(0, i - 1));
  }, []);

  const next = useCallback(() => {
    setConfirmingDelete(false);
    setIndex(i => Math.min(photos.length - 1, i + 1));
  }, [photos.length]);

  // Keyboard navigation
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (confirmingDelete) setConfirmingDelete(false);
        else onClose();
      }
      if (e.key === 'ArrowLeft')  prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, prev, next, confirmingDelete]);

  if (!photo) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
      onClick={onClose}
    >
      {/* Stop click propagation so clicking the image area doesn't close */}
      <div
        className="relative flex flex-col items-center max-w-3xl w-full px-4"
        onClick={e => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute -top-10 right-4 text-gray-400 hover:text-white text-3xl leading-none"
          aria-label="Close"
        >
          ×
        </button>

        {/* Image */}
        <img
          src={objectUrls[photo.photoId] ?? ''}
          alt={photo.originalFileName ?? 'Check-in photo'}
          className="max-h-[75vh] max-w-full rounded-lg object-contain"
        />

        {/* Navigation row */}
        <div className="flex items-center justify-between w-full mt-4 gap-4">
          <button
            onClick={prev}
            disabled={index === 0}
            className="px-4 py-2 rounded bg-dark-700 text-white disabled:opacity-30 hover:bg-dark-600 transition"
            aria-label="Previous photo"
          >
            ‹ Prev
          </button>

          <span className="text-sm text-gray-400">
            {index + 1} / {photos.length}
            {photo.originalFileName && (
              <span className="ml-2 text-gray-600 truncate max-w-xs">{photo.originalFileName}</span>
            )}
          </span>

          <button
            onClick={next}
            disabled={index === photos.length - 1}
            className="px-4 py-2 rounded bg-dark-700 text-white disabled:opacity-30 hover:bg-dark-600 transition"
            aria-label="Next photo"
          >
            Next ›
          </button>
        </div>

        {/* Delete — athletes only */}
        {onDelete && (
          <div className="mt-3 flex flex-col items-center gap-2 w-full max-w-sm">
            {!confirmingDelete ? (
              <button
                onClick={() => setConfirmingDelete(true)}
                className="px-4 py-1.5 rounded text-sm bg-red-900/60 text-red-300 hover:bg-red-800/80 transition"
              >
                Delete photo
              </button>
            ) : (
              <div className="w-full rounded-lg border border-red-500/40 bg-red-950/30 p-4 text-center">
                <p className="text-sm text-red-200 mb-3">
                  Delete this photo? This cannot be undone.
                </p>
                <div className="flex justify-center gap-3">
                  <button
                    onClick={() => { onDelete(photo); setConfirmingDelete(false); }}
                    className="px-4 py-1.5 rounded text-sm font-semibold bg-red-700 hover:bg-red-600 text-white transition"
                  >
                    Yes, Delete
                  </button>
                  <button
                    onClick={() => setConfirmingDelete(false)}
                    className="px-4 py-1.5 rounded text-sm font-semibold bg-dark-700 hover:bg-dark-600 border border-dark-500 text-gray-300 transition"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PhotoLightbox;
